const PGAdapter = require('../common/pg-adapter')
const PAYMENT_PLAN_OPTIONS_COLUMNS = [
  'id',
  'order_id',
  'service_fee_percentage',
  'frequency',
  'term',
  'location_id',
  'created_at',
  'updated_at'
]
const { LOCATION_CLASSIFICATIONS, MEMBERSHIPS } = require('../location/tools')
const {
  PAYMENT_PLAN_FREQUENCIES,
  PAYMENT_OPTION_BASE_ORDER_AMOUNT,
  PAYMENT_PLAN_OPTIONS_TABLE_NAME,
  CLINICS_AND_HOSPITALS_DEFAULT_FEE_PERCENTAGE,
  CLINICS_AND_HOSPITALS_DEFAULT_FREQUENCY,
  CLINICS_AND_HOSPITALS_DEFAULT_PAYMENT_TERM
} = require('./constants')
const { getPamentPlanOptionQuery, getPaymentOptionViaTxnIdQuery } = require('./queries/payment-option-queries')
const get = require('lodash/get')

class PaymentPlanOptionsAdapter extends PGAdapter {
  constructor (pgConnection, username) {
    super(
      pgConnection,
      PAYMENT_PLAN_OPTIONS_TABLE_NAME,
      username,
      PAYMENT_PLAN_OPTIONS_COLUMNS
    )
  }

  generatePaymentPlanOptions ({ countryCode, classification, orderAmount, membershipType, has60And90DayOption }) {
    const error = new Error()
    error.status = 400
    if (!Object.values(LOCATION_CLASSIFICATIONS).includes(classification)) {
      error.message = 'Invalid location classification'
      throw error
    }
    if (!['KE', 'NG'].includes(countryCode)) {
      error.message = 'Invalid country code'
      throw error
    }
    if (membershipType !== MEMBERSHIPS.CLASSIC_PAYG_ONLY) {
      // no payment plan options should be returned for non MC clients
      return {}
    }

    const defaultWeeklyTerms = {
      frequency: PAYMENT_PLAN_FREQUENCIES.WEEKLY,
      serviceFeePercentage: 0
    }

    const paymentTerms = {
      WEEKLY: {},
      FIFTEEN_DAYS: {},
      FORTY_FIVE_DAYS: {},
      MONTHLY: {},
      BIMONTHLY: {},
      TRIMONTHLY: {}
    }

    paymentTerms.WEEKLY = {
      4: {
        term: 4,
        ...defaultWeeklyTerms
      },
      6: {
        term: 6,
        ...defaultWeeklyTerms
      },
      8: {
        term: 8,
        frequency: PAYMENT_PLAN_FREQUENCIES.WEEKLY,
        serviceFeePercentage: 0.25
      }
    }

    const twelveWeekBaseAmount = get(PAYMENT_OPTION_BASE_ORDER_AMOUNT[countryCode], 'weekly.12.amount')
    const twentyFourWeekBaseAmount = get(PAYMENT_OPTION_BASE_ORDER_AMOUNT[countryCode], 'weekly.24.amount')
    const fifteenDaysBaseAmount = get(PAYMENT_OPTION_BASE_ORDER_AMOUNT[countryCode], 'fixedTerms.FIFTEEN_DAYS.amount')
    const monthlyBaseAmount = get(PAYMENT_OPTION_BASE_ORDER_AMOUNT[countryCode], 'fixedTerms.MONTHLY.amount')

    // 15 day fixed term
    if (
      (fifteenDaysBaseAmount && (orderAmount >= fifteenDaysBaseAmount)) ||
      (fifteenDaysBaseAmount && classification === LOCATION_CLASSIFICATIONS.CLINICS_AND_HOSPITALS)
    ) {
      paymentTerms.FIFTEEN_DAYS = {
        1: {
          term: 1,
          frequency: PAYMENT_PLAN_FREQUENCIES.FIFTEEN_DAYS,
          serviceFeePercentage: 0
        }
      }
    }

    // monthly fixed term
    if (
      (monthlyBaseAmount && (orderAmount >= monthlyBaseAmount)) ||
      (monthlyBaseAmount && classification === LOCATION_CLASSIFICATIONS.CLINICS_AND_HOSPITALS)
    ) {
      paymentTerms.MONTHLY = {
        1: {
          term: 1,
          frequency: PAYMENT_PLAN_FREQUENCIES.MONTHLY,
          serviceFeePercentage: 0
        }
      }
    }

    // 12 week terms
    if (
      (orderAmount >= twelveWeekBaseAmount && orderAmount < twentyFourWeekBaseAmount) ||
      (classification === LOCATION_CLASSIFICATIONS.CLINICS_AND_HOSPITALS)
    ) {
      paymentTerms.WEEKLY = {
        ...paymentTerms.WEEKLY,
        12: {
          term: 12,
          frequency: PAYMENT_PLAN_FREQUENCIES.WEEKLY,
          serviceFeePercentage: 0.5
        }
      }
    }

    // 24 week terms
    if (
      (orderAmount >= twentyFourWeekBaseAmount) ||
      (classification === LOCATION_CLASSIFICATIONS.CLINICS_AND_HOSPITALS)
    ) {
      paymentTerms.WEEKLY = {
        ...paymentTerms.WEEKLY,
        12: {
          term: 12,
          frequency: PAYMENT_PLAN_FREQUENCIES.WEEKLY,
          serviceFeePercentage: 0.5
        },
        24: {
          term: 24,
          frequency: PAYMENT_PLAN_FREQUENCIES.WEEKLY,
          serviceFeePercentage: countryCode === 'KE' ? 1.5 : 1.75
        }
      }
    }

    if (classification === LOCATION_CLASSIFICATIONS.CLINICS_AND_HOSPITALS) {
      paymentTerms.MONTHLY = {
        1: {
          term: CLINICS_AND_HOSPITALS_DEFAULT_PAYMENT_TERM,
          frequency: CLINICS_AND_HOSPITALS_DEFAULT_FREQUENCY,
          serviceFeePercentage: countryCode === 'KE'
            ? CLINICS_AND_HOSPITALS_DEFAULT_FEE_PERCENTAGE.KE
            : CLINICS_AND_HOSPITALS_DEFAULT_FEE_PERCENTAGE.NG
        }
      }

      if (has60And90DayOption) {
        paymentTerms.BIMONTHLY = {
          1: {
            term: 1,
            frequency: PAYMENT_PLAN_FREQUENCIES.BIMONTHLY,
            serviceFeePercentage: countryCode === 'KE' ? 0.5 : 1
          }
        }
        paymentTerms.TRIMONTHLY = {
          1: {
            term: 1,
            frequency: PAYMENT_PLAN_FREQUENCIES.TRIMONTHLY,
            serviceFeePercentage: countryCode === 'KE' ? 1.5 : 1.75
          }
        }
      }

      // 45 day fixed term payment plan option only available for Kenya clinic/hospitals
      if (countryCode === 'KE') {
        paymentTerms.FORTY_FIVE_DAYS = {
          1: {
            term: 1,
            frequency: PAYMENT_PLAN_FREQUENCIES.FORTY_FIVE_DAYS,
            serviceFeePercentage: 0.33
          }
        }
      }
    }

    return paymentTerms
  }

  /**
    * @param {string} locationUuid
  */
  async hasPrior60Or90dayFrequency (locationUuid) {
    const { rows: [row] } = await this.pgConnection.query(`
      select
        uuid
      from avocado.data_location 
      where (
        has_60_day_payment_option is true or
        has_90_day_payment_option is true
      ) and uuid = $1
    `, [locationUuid])
    return !!row
  }

  async getPaymentPlanOption (orderId, locationUuid) {
    if (!orderId || !locationUuid) {
      throw new Error('orderId & locationId required')
    }
    const { rows } = await this.pgConnection.query(getPamentPlanOptionQuery, [orderId, locationUuid])
    return rows[0]
  }

  async getPaymentPlanOptionViaTxnId (txnId, companyCode, locationUuid) {
    if (!txnId || !companyCode) {
      throw new Error('invoice txnId and companyCode required')
    }
    const params = [txnId, companyCode]
    if (locationUuid) {
      params.push(locationUuid)
    }
    const { rows } = await this.pgConnection.query(getPaymentOptionViaTxnIdQuery('$1', '$2', locationUuid), params)
    return rows[0]
  }
}

module.exports = PaymentPlanOptionsAdapter
